import React from 'react';
import { useSystemData } from '../contexts/SystemContext';
import { Activity, Users, FileText, Server, TrendingUp, RefreshCw } from 'lucide-react';

const Dashboard: React.FC = () => {
  const { 
    analytics, 
    systemStatus, 
    isLoading, 
    lastUpdated, 
    forceRefreshData
  } = useSystemData();

  const stats = [
    {
      name: 'Total Requests',
      value: analytics?.analytics.total_requests || 0,
      icon: Activity,
      color: 'bg-blue-500',
    },
    {
      name: 'Active Sessions',
      value: analytics?.active_sessions_count || 0,
      icon: Users,
      color: 'bg-green-500',
    },
    {
      name: 'Total Users',
      value: analytics?.analytics.total_unique_users || 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
    },
    {
      name: 'Available Prompts',
      value: systemStatus?.notion_connection?.prompt_count || 0,
      icon: FileText,
      color: 'bg-orange-500',
    },
  ];

  if (isLoading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-600">
            Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : 'Never'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">Data refreshes every 2 minutes</span>
          <button
            onClick={forceRefreshData}
            disabled={isLoading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="stat-card">
              <div className="flex items-center">
                <div className={`${stat.color} rounded-lg p-3`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* System Health */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">System Health</h3>
          <Server className="h-5 w-5 text-gray-400" />
        </div>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Status:</span>
            <span className={`text-sm font-medium ${
              systemStatus?.system.status === 'running' ? 'text-green-600' : 'text-red-600'
            }`}>
              {systemStatus?.system.status || 'Unknown'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Active Sessions:</span>
            <span className="text-sm font-medium">{systemStatus?.system.active_sessions || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Memory Usage:</span>
            <span className="text-sm font-medium">{systemStatus?.system.memory_usage || 0} %</span>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
        <div className="h-48 overflow-y-auto">
          <div className="space-y-3">
            {analytics && Object.entries(analytics.session_details).slice(0, 5).map(([sessionId, details]) => (
              <div key={sessionId} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate block">
                    Session: {sessionId.length > 8 ? `${sessionId.substring(0, 8)}...` : sessionId}
                  </span>
                  <span className="text-sm text-gray-500">
                    {details.message_count} messages
                  </span>
                </div>
                <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                  {details.last_activity ? 'Active' : 'Idle'}
                </span>
              </div>
            ))}
            {!analytics || Object.keys(analytics.session_details).length === 0 && (
              <div className="flex items-center justify-center h-32">
                <p className="text-gray-500 text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard; 